import { Flex, Tag } from '@chakra-ui/react';
import { useCustomersContext } from '../context';
import { useSearchParams } from 'react-router';
import { formatPhoneNumber } from '@utils/formats';

const PARAMS_LABELS = {
  name: 'Nome',
  email: 'E-mail',
  taxId: 'CPF',
  registration: 'Matrícula',
  tenure: 'Vínculo',
};

type FilterTagProps = {
  label: string;
  value: string;
  onRemove: () => void;
};

const FilterTag = ({ label, value, onRemove }: FilterTagProps) => (
  <Tag.Root size="lg" colorPalette="argos" variant="solid">
    <Tag.Label>
      <span style={{ fontSize: '0.75rem' }}>
        {label}:
      </span>
      {' '}
      <b>
        {value}
      </b>
    </Tag.Label>
    <Tag.EndElement>
      <Tag.CloseTrigger onClick={onRemove} cursor="pointer" />
    </Tag.EndElement>
  </Tag.Root>
);

const SelectedFiltersList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tags = searchParams.get('tags')?.split(',') || [];
  const phoneNumbers = searchParams.get('phoneNumbers')?.split(',') || [];

  const params = Object.fromEntries(searchParams.entries());
  const paramsKeys = Object.keys(params).filter((key) => !['portfolioId', 'limit', 'page', 'tags', 'phoneNumbers'].includes(key));

  const { portfolioDetails, getCustomers } = useCustomersContext();

  const onRemoveFilter = async (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);

    const filteredParams = Object.fromEntries(newParams.entries());

    await getCustomers({
      ...filteredParams,
    });
  };

  const onRemoveTag = async (tag: string) => {
    const newParams = new URLSearchParams(searchParams);
    const updatedTags = tags.filter((t) => t !== tag);
    if (updatedTags.length === 0) {
      newParams.delete('tags');
    } else {
      newParams.set('tags', updatedTags.join(','));
    }
    setSearchParams(newParams);

    const filteredParams = Object.fromEntries(newParams.entries());

    await getCustomers({
      ...filteredParams,
    });
  };

  const onRemovePhoneNumber = async (phone: string) => {
    const newParams = new URLSearchParams(searchParams);
    const updatedPhones = phoneNumbers.filter((p) => p !== phone);
    if (updatedPhones.length === 0) {
      newParams.delete('phoneNumbers');
    } else {
      newParams.set('phoneNumbers', updatedPhones.join(','));
    }
    setSearchParams(newParams);

    const filteredParams = Object.fromEntries(newParams.entries());

    await getCustomers({
      ...filteredParams,
    });
  };

  return (
    <Flex gap={2}>
      {paramsKeys.map((key) => (
        <FilterTag
          key={key}
          label={PARAMS_LABELS[key as keyof typeof PARAMS_LABELS]}
          value={params[key]}
          onRemove={() => onRemoveFilter(key)}
        />
      ))}
      {portfolioDetails && params.portfolioId && (
        <FilterTag
          label="Portfólio"
          value={portfolioDetails.name}
          onRemove={() => onRemoveFilter('portfolioId')}
        />
      )}
      {tags.map((tag) => (
        <FilterTag
          key={tag}
          label="Tag"
          value={tag}
          onRemove={() => onRemoveTag(tag)}
        />
      ))}
      {phoneNumbers.map((phone) => (
        <FilterTag
          key={phone}
          label="Telefone"
          value={formatPhoneNumber(phone)}
          onRemove={() => onRemovePhoneNumber(phone)}
        />
      ))}
    </Flex>
  )
};

export default SelectedFiltersList;
